import React, {
	useEffect,
	useState,
	useMemo,
	useRef,
	useCallback,
} from "react";
import { PageContainer } from "../components/Container/PageContainer";
import { Box, Button } from "@mui/material";
import { KeyMetricSummarySection } from "../template/metric/KeyMetricSummarySection";
import { WeeklyPostSectionCustomGrid } from "../template/post/WeeklyPostSectionCustomGrid";
import { trackEvent } from "../utils/analytics";
import errorLogger from "../utils/errorLogger";
import { DataLoadError } from "../components/error/DataLoadError";
import { useTranslation } from "react-i18next";
import { useDashboardTimeline } from "../contexts/ThreadsContext";
import { format } from "date-fns";
import { useIsMobile } from "../hooks/useIsMobile";
import { useMonthVisibility } from "../hooks/useMonthVisibility";
import DiscordCommunityBanner from "../components/banner/DiscordCommunityBanner";
import YearEndBanner from "../components/banner/YearEndBanner";
import { isReauthenticationRequired } from "../utils/authErrors";
import { clearLocalSession } from "../utils/tokenManager";

const Dashboard = ({ profile, userLoading, error }) => {
	const { t } = useTranslation();

	// 로컬 상태 (UI 관리용) - 모든 hooks를 상단에 배치
	const [dataError, setDataError] = useState(null);
	const [isDataLoadFailed, setIsDataLoadFailed] = useState(false);
	const [requiresReauth, setRequiresReauth] = useState(false);

	// 반응형 Hook
	const isSM = useIsMobile("sm");

	// 현재 표시 중인 월/년 상태
	const today = new Date();
	const [displayMonth, setDisplayMonth] = useState(format(today, "MM"));
	const [displayYear, setDisplayYear] = useState(format(today, "yyyy"));

	// Refs (스크롤 관련 ref 제거)
	const lastElementRef = useRef(null);

	// Timeline hooks 사용 - 무조건 호출해야 함 (Hook 규칙)
	const timelineHookResult = useDashboardTimeline();

	// Fallback values for when hook fails or returns incomplete data
	const safeTimelineData =
		timelineHookResult && typeof timelineHookResult === "object"
			? timelineHookResult
			: {
					timeline: {
						months: [],
						isLoadingMore: false,
						hasMoreFromAPI: false,
						hasMoreFromLifetime: false,
					},
					isInitialPhase: true,
					hasTimelineData: false,
					loadMore: async () => false,
					initializeTimeline: async () => {},
			  };

	const {
		timeline,
		isInitialPhase,
		hasTimelineData,
		loadMore,
		initializeTimeline,
	} = safeTimelineData;

	// 현재 표시할 월의 인사이트 데이터 계산
	const currentMonthInsight = useMemo(() => {
		if (!hasTimelineData || !timeline?.months || timeline.months.length === 0)
			return null;

		const targetMonth = parseInt(displayMonth);
		const targetYear = parseInt(displayYear);

		return (
			timeline.months.find(
				(item) => item && item.month === targetMonth && item.year === targetYear
			)?.insight || null
		);
	}, [timeline?.months, displayMonth, displayYear, hasTimelineData]);

	// 현재 달에 스레드가 있는지 확인
	const hasThreadsInCurrentMonth = useMemo(() => {
		if (!hasTimelineData || !timeline?.months || timeline.months.length === 0)
			return false;

		const targetMonth = parseInt(displayMonth);
		const targetYear = parseInt(displayYear);

		const monthData = timeline.months.find(
			(item) => item && item.month === targetMonth && item.year === targetYear
		);

		return (
			monthData &&
			monthData.threads?.week &&
			monthData.threads.week.length > 0 &&
			monthData.threads.week.some((week) => week.posts && week.posts.length > 0)
		);
	}, [timeline?.months, displayMonth, displayYear, hasTimelineData]);

	// 월별 스레드 데이터 변환 (기존 컴포넌트와 호환)
	const threadsList = useMemo(() => {
		if (!hasTimelineData || !timeline.months || timeline.months.length === 0)
			return [];

		return timeline.months
			.filter(
				(monthData) =>
					monthData &&
					monthData.month !== undefined &&
					monthData.year !== undefined
			)
			.map((monthData) => ({
				month: monthData.month.toString().padStart(2, "0"),
				year: monthData.year.toString(),
				week: monthData.threads?.week || [],
			}))
			.sort((a, b) => {
				// 최신 월부터 정렬
				if (a.year !== b.year) return parseInt(b.year) - parseInt(a.year);
				return parseInt(b.month) - parseInt(a.month);
			});
	}, [timeline?.months, hasTimelineData]);

	// 로딩 상태 계산 - 개선된 로직
	const isLoadingMore = timeline.isLoadingMore;
	const isInitialLoading = isInitialPhase && threadsList.length === 0;

	const hasMoreData = timeline.hasMoreFromAPI;

	// 메트릭 섹션 로딩 상태
	const isMetricLoading = isInitialLoading;

	// 데이터 재시도 함수
	const retryDataLoad = async () => {
		setDataError(null);
		setIsDataLoadFailed(false);
		setRequiresReauth(false);

		try {
			await initializeTimeline();
		} catch (error) {
			console.error("Dashboard data retry failed:", error);
			setRequiresReauth(isReauthenticationRequired(error));
			setDataError(error.message || t("error.dataLoadFailed"));
			setIsDataLoadFailed(true);
			errorLogger.log({
				component: "Dashboard",
				event: "retry_failed",
				endpoint: error?.config?.url,
				status: error?.response?.status,
				errorCode: error?.code,
				tokenPresent:
					!!localStorage.getItem("token") ||
					!!localStorage.getItem("access_token"),
			});
		}
	};

	const reconnectThreads = () => {
		clearLocalSession();
		window.location.assign("/");
	};

	// 이전 월 로드 함수 (Timeline에 새로운 month push)
	const onLoadPreviousMonth = async () => {
		try {
			console.log(`📅 Dashboard: 이전 월 데이터 로드 시작`);
			console.log(`📅 Timeline 상태:`, {
				hasMoreFromAPI: timeline.hasMoreFromAPI,
				isLoadingMore: timeline.isLoadingMore,
				monthsCount: timeline.months?.length || 0,
			});

			const success = await loadMore();
			if (success) {
				console.log(`✅ Dashboard: 이전 월 로드 요청 성공`);
			} else {
				console.log(`ℹ️ Dashboard: 더 이상 로드할 데이터가 없음`);
				console.log(`ℹ️ 현재 Timeline 상태:`, {
					hasMoreFromAPI: timeline.hasMoreFromAPI,
				});
			}
		} catch (error) {
			console.error(`❌ Dashboard: 이전 월 로드 실패:`, error);
			setRequiresReauth(isReauthenticationRequired(error));
			setDataError(error.message || t("error.dataLoadFailed"));
			errorLogger.log({
				component: "Dashboard",
				event: "load_previous_month_failed",
				endpoint: error?.config?.url,
				status: error?.response?.status,
				errorCode: error?.code,
				tokenPresent:
					!!localStorage.getItem("token") ||
					!!localStorage.getItem("access_token"),
			});
		}
	};

	// 월 가시성 변경 핸들러
	const handleMonthVisibilityChange = useCallback(
		({ year, month }) => {
			if (displayYear !== year || displayMonth !== month) {
				console.log(
					`📍 Dashboard: 화면에 가장 많이 표시되는 월 변경: ${year}-${month}`
				);
				setDisplayMonth(month);
				setDisplayYear(year);
			}
		},
		[displayYear, displayMonth]
	);

	// 월 가시성 추적 훅 사용
	const { registerRef } = useMonthVisibility(
		threadsList,
		handleMonthVisibilityChange,
		{
			topOffset: 120, // 상단 120px 지나면 "사라짐" (메트릭 섹션 고려)
			bottomOffset: 150, // 하단 150px 들어오면 "나타남"
			transitionPoint: 0.3, // 30% 지점에서 전환
		}
	);

	// Timeline 상태 변화 모니터링 (디버깅용)
	useEffect(() => {
		console.log(`📊 Dashboard: Timeline 상태 업데이트됨`, {
			monthsCount: timeline.months?.length || 0,
			threadsListLength: threadsList.length,
			hasTimelineData,
			isLoadingMore: timeline.isLoadingMore,
		});
	}, [
		timeline.months,
		threadsList.length,
		hasTimelineData,
		timeline.isLoadingMore,
	]);

	// 새로운 월 추가 감지
	useEffect(() => {
		if (timeline.months && timeline.months.length > 0) {
			const monthKeys = timeline.months
				.filter((m) => m && m.year && m.month)
				.map((m) => `${m.year}-${m.month.toString().padStart(2, "0")}`);
			console.log(`📅 Dashboard: 현재 로드된 월들:`, monthKeys);
		}
	}, [timeline.months?.length]);

	// 가장 최근 달(현재 달)에만 글이 없을 때 자동으로 이전 달 불러오기
	useEffect(() => {
		const autoLoadPreviousMonth = async () => {
			// 현재 날짜와 비교
			const isCurrentMonth =
				displayYear === format(today, "yyyy") &&
				displayMonth === format(today, "MM");

			// 조건 확인:
			// 1. 초기 로딩이 완료됨
			// 2. 현재 달(오늘 날짜)에 스레드가 없음
			// 3. 더 불러올 데이터가 있음
			// 4. 현재 로딩 중이 아님
			// 5. 현재 달인 경우에만
			if (
				!isInitialLoading &&
				hasTimelineData &&
				!hasThreadsInCurrentMonth &&
				timeline.hasMoreFromAPI &&
				!timeline.isLoadingMore &&
				isCurrentMonth
			) {
				console.log(
					`📅 ${displayYear}년 ${displayMonth}월(현재 달)에 글이 없어 자동으로 이전 달을 불러옵니다.`
				);

				try {
					await loadMore();
				} catch (error) {
					console.error("자동 이전 달 로딩 실패:", error);
				}
			}
		};

		// 조금의 지연을 두고 실행 (UI 업데이트 완료 후)
		const timeoutId = setTimeout(autoLoadPreviousMonth, 500);
		return () => clearTimeout(timeoutId);
	}, [
		isInitialLoading,
		hasTimelineData,
		hasThreadsInCurrentMonth,
		timeline.hasMoreFromAPI,
		timeline.isLoadingMore,
		displayYear,
		displayMonth,
		loadMore,
		today,
	]);

	// 초기 타임라인 로드 (오늘 날짜 월부터 시작)
	useEffect(() => {
		// Storybook mock 데이터 처리
		if (window.mockMonthlyThreadsData && window.mockMonthlyInsightData) {
			// Timeline에 mock 데이터 추가하는 로직 필요
			// 현재는 mock 환경에서 스킵
			return;
		}

		if (isInitialPhase && !userLoading && !error && timelineHookResult) {
			console.log(
				`🚀 Dashboard: 오늘 날짜(${displayYear}-${displayMonth}) 월별 데이터 초기 로드 시작`
			);

			initializeTimeline().catch((error) => {
				console.error("Timeline initialization failed:", error);
				setRequiresReauth(isReauthenticationRequired(error));
				setDataError(error.message || t("error.dataLoadFailed"));
				setIsDataLoadFailed(true);
				const responseData = error?.responseData || error?.response?.data;
				const endpoint =
					error?.endpoint ||
					error?.config?.url ||
					error?.response?.config?.url ||
					error?.request?.responseURL;
				errorLogger.log({
					component: "Dashboard",
					event: "initial_load_failed",
					endpoint,
					status: error?.status || error?.response?.status,
					errorCode: error?.code,
					tokenPresent:
						!!localStorage.getItem("token") ||
						!!localStorage.getItem("access_token") ||
						!!localStorage.getItem("longLivedToken"),
					month: Number(displayMonth),
					year: Number(displayYear),
					extra: responseData
				});
			});
		}
	}, [
		isInitialPhase,
		userLoading,
		error,
		initializeTimeline,
		timelineHookResult,
		displayYear,
		displayMonth,
		t,
	]);

	// 스크롤 조정 제거 - Main에서 스크롤 제어

	// Hook error 처리
	if (!timelineHookResult || typeof timelineHookResult !== "object") {
		errorLogger.log({
			component: "Dashboard",
			event: "timeline_hook_error",
			phase: undefined,
			stack: "timelineHookResult invalid",
		});
		return (
			<PageContainer>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						px: 3,
						py: 3,
					}}
				>
					<DataLoadError
						title={t("error.timelineHookErrorTitle")}
						message={t("error.timelineHookErrorMessage")}
						showRetry={true}
						onRetry={() => window.location.reload()}
					/>
				</Box>
			</PageContainer>
		);
	}

	// 사용자 인증 상태 처리
	if (userLoading) {
		return (
			<PageContainer>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						px: 3,
						py: 3,
					}}
				>
					<div>사용자 정보를 불러오는 중...</div>
				</Box>
			</PageContainer>
		);
	}

	if (error) {
		errorLogger.log({
			component: "Dashboard",
			event: "auth_error",
			status: 401,
			errorCode: "unauthorized",
			tokenPresent:
				!!localStorage.getItem("token") ||
				!!localStorage.getItem("access_token"),
		});
		return (
			<PageContainer>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						px: 3,
						py: 3,
					}}
				>
					<DataLoadError
						title={t("error.unauthorized")}
						message={error}
						showRetry={false}
					/>
				</Box>
			</PageContainer>
		);
	}

	// 초기 로딩 상태 - 스켈레톤 UI로 일관성 유지
	if (isInitialLoading && !isDataLoadFailed) {
		return (
			<PageContainer>
				<KeyMetricSummarySection
					loading={true}
					year={displayYear || ""}
					month={displayMonth || ""}
					profileViews={0}
					views={0}
					likes={0}
					replies={0}
					reposts={0}
					postLoading={true}
					monthlyViews={[]}
					dataError={null}
				/>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						px: 3,
						py: 3,
					}}
				>
					<div>
						{displayYear}년 {displayMonth}월 데이터를 불러오는 중...
					</div>
				</Box>
			</PageContainer>
		);
	}

	if (isDataLoadFailed && threadsList.length === 0) {
		return (
			<PageContainer>
				{/* <KeyMetricSummarySection
          loading={false}
          year={displayYear || ""}
          month={displayMonth || ""}
          profileViews={0}
          views={0}
          likes={0}
          replies={0}
          reposts={0}
          postLoading={false}
          monthlyViews={[]}
          dataError={dataError}
        /> */}
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						px: 3,
						py: 3,
					}}
				>
						<DataLoadError
							title={requiresReauth ? "Threads 연결이 만료되었습니다" : t("error.dataLoadFailed")}
							message={requiresReauth ? "저장된 연결도 더 이상 유효하지 않습니다. Threads 계정을 다시 연결해주세요." : t("error.threadsDataLoadFailed")}
							showRetry={true}
							onRetry={requiresReauth ? reconnectThreads : retryDataLoad}
							actionLabel={requiresReauth ? "Threads 다시 연결" : undefined}
						/>
				</Box>
			</PageContainer>
		);
	}

	return (
		<PageContainer>
			<KeyMetricSummarySection
				loading={isMetricLoading}
				year={displayYear || ""}
				month={displayMonth || ""}
				profileViews={
					currentMonthInsight?.profileViews?.reduce(
						(sum, item) => sum + item.y,
						0
					) || 0
				}
				views={currentMonthInsight?.views || 0}
				likes={currentMonthInsight?.likes || 0}
				replies={currentMonthInsight?.replies || 0}
				reposts={currentMonthInsight?.reposts || 0}
				postLoading={isMetricLoading}
				monthlyViews={currentMonthInsight?.profileViews || []}
				dataError={dataError}
			/>
			<Box
				id={"container"}
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 3,
					pt: isSM ? "224px" : "264px",
					width: "100%",
				}}
			>
				{/* Year End Banner - 연말정산 참여 유도 */}
				<Box sx={{ width: "100%", mb: 2 }}>
					<YearEndBanner />
				</Box>

				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 0,
						pb: 0,
						width: "100%",
					}}
				>
					{threadsList.map((monthlyData, i) => {
						// 해당 월에 스레드가 있는지 확인
						const hasThreads =
							monthlyData.week &&
							monthlyData.week.length > 0 &&
							monthlyData.week.some(
								(week) => week.posts && week.posts.length > 0
							);

						const monthKey = `${monthlyData.year}-${monthlyData.month}`;

						return (
							<Box
								sx={{ display: "flex", flexDirection: "column" }}
								ref={(el) => {
									if (el) {
										registerRef(el, monthKey);
									}
									if (i === threadsList.length - 1) {
										lastElementRef.current = el;
									}
								}}
								key={monthKey}
							>
								{hasThreads ? (
									<>
										<WeeklyPostSectionCustomGrid
											key={i}
											threadsList={monthlyData}
											profile={profile}
											setDisplayMonth={setDisplayMonth}
											setDisplayYear={setDisplayYear}
										/>
										{/* Discord Community Banner - 첫 번째 월 다음 */}
										{i === 0 && (
											<Box sx={{ width: "100%", my: 3 }}>
												<DiscordCommunityBanner />
											</Box>
										)}
									</>
								) : (
									// 글이 없는 달 안내 메시지 - 현재 달인 경우에만 표시
									(() => {
										const isCurrentMonth =
											monthlyData.year === format(today, "yyyy") &&
											monthlyData.month === format(today, "MM");

										// 현재 달이 아닌 경우 아무것도 표시하지 않음
										if (!isCurrentMonth) {
											return null;
										}

										return (
											<Box
												sx={{
													display: "flex",
													flexDirection: "column",
													alignItems: "center",
													justifyContent: "center",
													py: 8,
													px: 3,
													textAlign: "center",
													color: "text.secondary",
													backgroundColor: "rgba(0, 0, 0, 0.02)",
													borderRadius: 2,
													mb: 3,
													minHeight: "200px",
												}}
											>
												<Box
													sx={{ fontSize: "1.2rem", fontWeight: 600, mb: 1 }}
												>
													📝
												</Box>
												<Box sx={{ fontSize: "1rem", lineHeight: 1.5 }}>
													{t("dashboard.noThreadsInMonth", {
														year: monthlyData.year,
														month: parseInt(monthlyData.month),
													})}
												</Box>
												{timeline.isLoadingMore && (
													<Box
														sx={{
															fontSize: "0.875rem",
															mt: 2,
															color: "primary.main",
														}}
													>
														{t("dashboard.autoLoadingPreviousMonth", {
															year: monthlyData.year,
															month: parseInt(monthlyData.month),
														})}
													</Box>
												)}
											</Box>
										);
									})()
								)}
							</Box>
						);
					})}
					{hasMoreData && (
						<Button
							size="large"
							variant="outlined"
							color="primary"
							onClick={() => {
								console.log(`🔄 Dashboard: 이전 달 불러오기 버튼 클릭`);
								onLoadPreviousMonth();
								trackEvent("load_previous_month");
							}}
							disabled={isLoadingMore}
							sx={{
								mt: 3,
								mb: 10,
							}}
						>
							{isLoadingMore ? "Loading..." : t("dashboard.loadPreviousMonth")}
						</Button>
					)}
				</Box>
			</Box>
		</PageContainer>
	);
};

export default Dashboard;
